import {
  AtomErrors,
  AuthErrors,
  InternalErrors,
  PrismaErrors,
  UserServiceErrors,
} from "@atoms/atom-types";
import type { ApiOperationErrorContext } from "../apiRequestOptions";
import { getLocale, translate } from "../i18n/TranslationBackendRegistry";
import { getErrorClassDisplay, isProjectErrorCode } from "./errorClassDisplay";
import { getDisplayedErrorRef } from "./errorDetails";
import { pickLocalizedText } from "./localizedText";
import { type NormalizedError, normalizeError } from "./normalizeError";
import type { NotifyLevel } from "./notifier";
import { renderRuleMessage } from "./ruleMessage";

/**
 * What a toast, an error state or an error screen shows for an error: level,
 * title, the text below it and the key that dedupes repeated toasts.
 */
export type FormattedError = Readonly<{
  level: NotifyLevel;
  message: string;
  description?: string;
  key?: string;
}>;

type UserFacingCatalogEntry = Readonly<{
  level: NotifyLevel;
  /**
   * Explains the failure in the user's terms. Entries without one fall back to
   * the generic message plus the reference code, which is all we can say about
   * errors the user cannot act on.
   */
  messageKey?: string;
  messageDefault?: string;
}>;

type UserFacingOptions = Readonly<{
  defaultMessage?: string;
  keyPrefix?: string;
  operationContext?: ApiOperationErrorContext;
}>;

type UserFacingSuccessOptions = Readonly<{
  successMessage?: string;
  keyPrefix?: string;
  operationContext?: ApiOperationErrorContext;
}>;

const atomErrorCatalog = {
  [AtomErrors.CannotProvideAtom]: {
    level: "error",
  },
  [AtomErrors.AtomIsNotRespawnable]: {
    level: "warning",
  },
  [AtomErrors.InternalError]: {
    level: "error",
  },
  [AtomErrors.FailedToGetLock]: {
    level: "warning",
  },
  [AtomErrors.PersistenceServiceCriticalError]: {
    level: "error",
  },
  [AtomErrors.UniqueConstraintViolation]: {
    level: "warning",
    messageKey: "framework.errors.uniqueViolation",
    messageDefault: "Dieser Wert ist bereits vergeben.",
  },
  [AtomErrors.InsufficientPermission]: {
    level: "warning",
  },
  [AtomErrors.DeadLock]: {
    level: "warning",
  },
  [AtomErrors.LockTimedOut]: {
    level: "warning",
  },
} satisfies Record<AtomErrors, UserFacingCatalogEntry>;

const authErrorCatalog = {
  [AuthErrors.SignUpRequired]: {
    level: "warning",
  },
  [AuthErrors.MigrationRequired]: {
    level: "warning",
  },
  [AuthErrors.EmailNotVerified]: {
    level: "warning",
  },
} satisfies Record<AuthErrors, UserFacingCatalogEntry>;

const userServiceErrorCatalog = {
  [UserServiceErrors.InviteAlreadyAccepted]: {
    level: "warning",
    messageKey: "framework.errors.inviteAlreadyAccepted",
    messageDefault:
      "Die Einladung wurde bereits angenommen. Der Benutzer kann sich anmelden oder das Passwort zurücksetzen.",
  },
  [UserServiceErrors.EmailAlreadyLinkedToDifferentAuthProvider]: {
    level: "warning",
    messageKey: "framework.errors.emailAlreadyLinked",
    messageDefault:
      "Diese E-Mail-Adresse ist bereits mit einem anderen Benutzerkonto verknüpft.",
  },
  [UserServiceErrors.EmailLinkRequiresVerifiedMatchingProviderEmail]: {
    level: "warning",
    messageKey: "framework.errors.emailLinkRequiresVerifiedEmail",
    messageDefault:
      "Das Benutzerkonto kann erst verknüpft werden, wenn die E-Mail-Adresse bestätigt wurde.",
  },
  [UserServiceErrors.OperationNotSupported]: {
    level: "error",
    messageKey: "framework.errors.userServiceOperationNotSupported",
    messageDefault:
      "Diese Funktion wird vom Anmeldeanbieter nicht unterstützt.",
  },
} satisfies Record<UserServiceErrors, UserFacingCatalogEntry>;

const prismaErrorCatalog = {
  [PrismaErrors.StoredProcedureNotFound]: {
    level: "error",
  },
} satisfies Record<PrismaErrors, UserFacingCatalogEntry>;

const internalErrorCatalog = {
  [InternalErrors.NoActingUser]: {
    level: "error",
  },
  [InternalErrors.MissingContextEntry]: {
    level: "error",
  },
  [InternalErrors.InsufficientContext]: {
    level: "error",
  },
  [InternalErrors.CannotSpawnVirtualAtom]: {
    level: "error",
  },
  [InternalErrors.CannotSpawnWithoutLocking]: {
    level: "error",
  },
  [InternalErrors.CannotCommitWithoutLocking]: {
    level: "error",
  },
  [InternalErrors.CannotCommitTwice]: {
    level: "error",
  },
  [InternalErrors.CannotDirectlyChangeVirtualAtoms]: {
    level: "error",
  },
  [InternalErrors.InfiniteLoop]: {
    level: "error",
  },
  [InternalErrors.TryingToCommitFailedTransaction]: {
    level: "error",
  },
  [InternalErrors.ContextOverrideMismatch]: {
    level: "error",
  },
  [InternalErrors.HashMismatch]: {
    level: "warning",
  },
  [InternalErrors.MissingPayload]: {
    level: "error",
  },
  [InternalErrors.ErrorDuringTransformation]: {
    level: "error",
  },
  [InternalErrors.NoLockerSet]: {
    level: "error",
  },
  [InternalErrors.LockerNotFound]: {
    level: "error",
  },
  [InternalErrors.MissingModuleRef]: {
    level: "error",
  },
  [InternalErrors.ContextChanged]: {
    level: "warning",
  },
  [InternalErrors.InsufficientPermissions]: {
    level: "warning",
  },
  [InternalErrors.Persistence]: {
    level: "error",
  },
  [InternalErrors.Configuration]: {
    level: "error",
  },
  [InternalErrors.MissingDependency]: {
    level: "error",
  },
  [InternalErrors.NotFound]: {
    level: "warning",
  },
  [InternalErrors.Validation]: {
    level: "warning",
  },
  [InternalErrors.UnsupportedOperation]: {
    level: "warning",
  },
  [InternalErrors.Unavailable]: {
    level: "warning",
  },
  [InternalErrors.ProviderRequestFailed]: {
    level: "error",
  },
  [InternalErrors.InvariantViolation]: {
    level: "error",
  },
  [InternalErrors.UnknownEntityType]: {
    level: "warning",
  },
  [InternalErrors.WrongAtomKind]: {
    level: "warning",
  },
  [InternalErrors.Other]: {
    level: "error",
  },
  [InternalErrors.AtomHasNoRelation]: {
    level: "error",
  },
  [InternalErrors.Unknown]: {
    level: "error",
  },
} satisfies Record<InternalErrors, UserFacingCatalogEntry>;

// Resolved per call: translations are registered after this module is loaded.
const getGenericUserFacingErrorMessage = () =>
  translate(
    "framework.errors.requestFailed",
    "Die Anfrage konnte nicht abgeschlossen werden.",
  );
const formatReferenceCode = (code: string) =>
  translate("framework.errors.referenceCode", "Referenzcode: {{code}}", {
    code,
  });

function getCatalogExplanation(
  entry: UserFacingCatalogEntry | undefined,
): string | undefined {
  return entry?.messageKey && entry.messageDefault
    ? translate(entry.messageKey, entry.messageDefault)
    : undefined;
}

function formatCatalogEntry(
  entry: UserFacingCatalogEntry,
  keyPrefix: string,
  code?: string,
): FormattedError {
  const explanation = getCatalogExplanation(entry);

  return {
    level: entry.level,
    message: explanation ?? getGenericUserFacingErrorMessage(),
    // An explained error needs no reference code: it is nothing the user has
    // to report, and the code only adds noise.
    description: explanation || !code ? undefined : formatReferenceCode(code),
    key: code ? `${keyPrefix}:${code}` : `${keyPrefix}:known-error`,
  };
}

function formatCatalogCode(
  catalog: Readonly<Record<string, UserFacingCatalogEntry>>,
  catalogCode: string | undefined,
  keyPrefix: string,
  referenceCode = catalogCode,
): FormattedError | undefined {
  if (!catalogCode) {
    return undefined;
  }

  const entry = Object.hasOwn(catalog, catalogCode)
    ? catalog[catalogCode]
    : undefined;
  return entry
    ? formatCatalogEntry(entry, keyPrefix, referenceCode)
    : undefined;
}

/**
 * Level of an error that only carries its code. A one-shot request sends no
 * error type, so the atom error codes (permission, locks, …) would otherwise
 * all be shown as errors, unlike on a live query.
 */
function getLevelForUncataloguedCode(
  errorCode: string | undefined,
): NotifyLevel {
  const catalog: Readonly<Record<string, UserFacingCatalogEntry>> =
    atomErrorCatalog;
  return errorCode && Object.hasOwn(catalog, errorCode)
    ? (catalog[errorCode]?.level ?? "error")
    : "error";
}

/** The text of a `userFacing` record in the active language, if it has one. */
function getLocalizedText(
  text?: Readonly<Record<string, string>>,
): string | undefined {
  return pickLocalizedText(text, getLocale());
}

/** The catalogues that hold explained sentences. */
const explainedCatalogs: ReadonlyArray<
  Readonly<Record<string, UserFacingCatalogEntry>>
> = [userServiceErrorCatalog, atomErrorCatalog];

/**
 * The sentence the framework has for a code: those of the user service and
 * the one of a unique violation.
 */
function getExplanationForCode(
  errorCode: string | undefined,
): string | undefined {
  if (!errorCode) {
    return undefined;
  }
  for (const catalog of explainedCatalogs) {
    if (Object.hasOwn(catalog, errorCode)) {
      return getCatalogExplanation(catalog[errorCode]);
    }
  }
  return undefined;
}

function getErrorKey(
  keyPrefix: string,
  errorCode: string | undefined,
  hasOperationContext: boolean,
): string {
  if (errorCode) {
    return `${keyPrefix}:${errorCode}`;
  }
  return hasOperationContext
    ? `${keyPrefix}:operation-error`
    : `${keyPrefix}:unknown-error`;
}

/**
 * A code a project defines itself reaches the client with class `internal`
 * until the project throws it as a rule error. It is a rejected rule, not a
 * defect, so it keeps the text it had before classes existed.
 */
function isUnmigratedProjectRule(error: NormalizedError): boolean {
  return (
    error.errorClass === "internal" &&
    error.code !== undefined &&
    isProjectErrorCode(error.code)
  );
}

/** The titles an error can get, by where they come from. */
type ErrorTitles = Readonly<{
  /** The sentence of the failed operation, when the caller's context has one. */
  operation?: string;
  /** The caller's default message, else the generic sentence. */
  fallback: string;
}>;

type ComposedTexts = Readonly<{ title: string; cause?: string }>;

/** The class the backend gives a rule error of a project. */
const RULE_ERROR_CLASS = "rule";

/** The class the backend gives a unique violation. */
const UNIQUE_VIOLATION_ERROR_CLASS = "conflict";

/**
 * Title and text of a rule error that brings its own sentence: the title
 * says what failed, as for every other class, and the sentence of the
 * project stands below it, also when the action has no operation sentence
 * and the title is the generic one (decision O10 of atoms/atoms2#96,
 * option a). Option b returns `{ title: sentence }` when `titles.operation`
 * is absent.
 */
function composeRuleTexts(
  titles: ErrorTitles,
  sentence: string,
): ComposedTexts {
  return { title: titles.operation ?? titles.fallback, cause: sentence };
}

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;
const SENTENCE_HASH_LENGTH = 6;

/**
 * Six hex digits that tell two sentences apart (FNV-1a over the code
 * points). Not a cryptographic hash: it only keeps toasts with different
 * texts from being taken for the same one.
 */
function hashSentence(sentence: string): string {
  let hash = FNV_OFFSET_BASIS;
  for (const character of sentence) {
    hash = Math.imul(hash ^ (character.codePointAt(0) ?? 0), FNV_PRIME);
  }
  return (hash >>> 0)
    .toString(16)
    .padStart(8, "0")
    .slice(-SENTENCE_HASH_LENGTH);
}

/**
 * The sentence an error brings itself (`userMessage`): the sentence of a
 * rule error of a project, or the text the project's catalog has for the
 * constraint of a unique violation (`app.errors.unique.<constraint>`). The
 * message of a unique violation has no default text, so without a catalog
 * text there is none, and the sentence of the framework is shown. The key
 * itself is never shown.
 */
function getBroughtSentence(
  error: NormalizedError,
  errorClass: string,
): string | undefined {
  const bringsSentence =
    errorClass === RULE_ERROR_CLASS ||
    (errorClass === UNIQUE_VIOLATION_ERROR_CLASS &&
      error.code === AtomErrors.UniqueConstraintViolation);
  return bringsSentence ? renderRuleMessage(error.userMessage) : undefined;
}

/**
 * The text of an error the backend classified: the title says what failed,
 * the description why. The class decides the level; the reference is shown
 * where the user is expected to report the failure.
 *
 * A rule error of a project shows the project's sentence as the cause, and
 * a unique violation the sentence the project's catalog has for its
 * constraint. The dedupe key ends with a hash of that sentence: the same
 * rule rejected with other values is another toast, the same sentence twice
 * is one.
 */
function composeClassedError(
  error: NormalizedError,
  errorClass: string,
  titles: ErrorTitles,
  key: string,
): FormattedError {
  const display = getErrorClassDisplay(errorClass);
  const ruleSentence = getBroughtSentence(error, errorClass);
  const { title, cause }: ComposedTexts = ruleSentence
    ? composeRuleTexts(titles, ruleSentence)
    : {
        title: titles.operation ?? titles.fallback,
        cause:
          getExplanationForCode(error.code) ??
          (display
            ? translate(display.messageKey, display.messageDefault)
            : undefined),
      };
  // A class this client does not know is reported like a defect.
  const reference =
    (display?.showReference ?? true)
      ? getDisplayedErrorRef(error.errorRef)
      : undefined;
  const lines = [
    cause,
    reference
      ? translate("framework.errors.reference", "Referenz: {{ref}}", {
          ref: reference,
        })
      : undefined,
  ].filter((line): line is string => Boolean(line));

  return {
    level: display?.level ?? "error",
    message: title,
    description: lines.length > 0 ? lines.join("\n") : undefined,
    key: ruleSentence ? `${key}:${hashSentence(ruleSentence)}` : key,
  };
}

function getOperationContextMessage(
  context: ApiOperationErrorContext,
): string | undefined {
  // Explicit failure message always wins (used by both entity/atom overrides
  // and as the only failure source for custom reducers).
  const explicitFailureMessage = getLocalizedText(context.failureMessage);
  if (explicitFailureMessage) {
    return explicitFailureMessage;
  }

  // Reducers describe arbitrary actions — there is no sensible label-based
  // template, so fall back to the generic default if no explicit message exists.
  if (context.targetKind === "reducer") {
    return undefined;
  }

  const label = getLocalizedText(context.label);
  if (!label) {
    return undefined;
  }

  switch (context.operation) {
    case "create":
      return translate(
        "framework.errors.createFailed",
        "{{label}} konnte nicht erstellt werden.",
        { label },
      );
    case "delete":
      return translate(
        "framework.errors.deleteFailed",
        "{{label}} konnte nicht gelöscht werden.",
        { label },
      );
    case "exists":
      return translate(
        "framework.errors.existsFailed",
        "{{label}} konnte nicht gefunden werden.",
        { label },
      );
    case "update":
      return translate(
        "framework.errors.updateFailed",
        "{{label}} konnte nicht aktualisiert werden.",
        { label },
      );
    case "set":
      return translate(
        "framework.errors.setFailed",
        "{{label}} konnte nicht gespeichert werden.",
        { label },
      );
    case "addList":
      return translate(
        "framework.errors.addListFailed",
        "Eintrag für {{label}} konnte nicht hinzugefügt werden.",
        { label },
      );
    case "removeList":
      return translate(
        "framework.errors.removeListFailed",
        "Eintrag für {{label}} konnte nicht entfernt werden.",
        { label },
      );
    case "customReducer":
      // Unreachable: reducer kind is handled above. Kept for exhaustiveness.
      return undefined;
  }
}

function getOperationContextSuccessMessage(
  context: ApiOperationErrorContext,
): string | undefined {
  // Explicit success message always wins (the only source for reducers).
  const explicitSuccessMessage = getLocalizedText(context.successMessage);
  if (explicitSuccessMessage) {
    return explicitSuccessMessage;
  }

  // Reducers do not produce label-templated success phrases.
  if (context.targetKind === "reducer") {
    return undefined;
  }

  const label = getLocalizedText(context.label);
  if (!label) {
    return undefined;
  }

  switch (context.operation) {
    case "create":
      return translate(
        "framework.errors.createSuccess",
        "{{label}} erfolgreich erstellt.",
        { label },
      );
    case "delete":
      return translate(
        "framework.errors.deleteSuccess",
        "{{label}} erfolgreich gelöscht.",
        { label },
      );
    case "exists":
      return translate(
        "framework.errors.existsSuccess",
        "{{label}} erfolgreich gefunden.",
        { label },
      );
    case "update":
      return translate(
        "framework.errors.updateSuccess",
        "{{label}} erfolgreich aktualisiert.",
        { label },
      );
    case "set":
      return translate(
        "framework.errors.setSuccess",
        "{{label}} erfolgreich gespeichert.",
        { label },
      );
    case "addList":
      return translate(
        "framework.errors.addListSuccess",
        "Eintrag für {{label}} erfolgreich hinzugefügt.",
        { label },
      );
    case "removeList":
      return translate(
        "framework.errors.removeListSuccess",
        "Eintrag für {{label}} erfolgreich entfernt.",
        { label },
      );
    case "customReducer":
      // Unreachable: reducer kind is handled above. Kept for exhaustiveness.
      return undefined;
  }
}

/**
 * Resolves level, text, reference code and dedupe key for an error. Accepts
 * what was thrown or an error that `normalizeError` already read.
 */
export function resolveUserFacingError(
  error: unknown,
  options?: UserFacingOptions,
): FormattedError {
  // An empty default message counts as none: a toast never has an empty text.
  const defaultMessage =
    options?.defaultMessage || getGenericUserFacingErrorMessage();
  const keyPrefix = options?.keyPrefix ?? "err";
  const normalized: NormalizedError = normalizeError(error);
  const errorCode = normalized.code;
  const operationMessage = options?.operationContext
    ? getOperationContextMessage(options.operationContext)
    : undefined;
  const key = getErrorKey(
    keyPrefix,
    errorCode,
    options?.operationContext !== undefined,
  );

  // Composition starts when the backend sent a class. Without one (an older
  // backend, a network failure, an error of the client) the text below is
  // what it has always been.
  if (normalized.errorClass && !isUnmigratedProjectRule(normalized)) {
    return composeClassedError(
      normalized,
      normalized.errorClass,
      { operation: operationMessage, fallback: defaultMessage },
      key,
    );
  }

  const cataloguedError =
    formatCatalogCode(userServiceErrorCatalog, errorCode, keyPrefix) ??
    formatCatalogCode(authErrorCatalog, errorCode, keyPrefix) ??
    formatCatalogCode(prismaErrorCatalog, errorCode, keyPrefix) ??
    formatCatalogCode(internalErrorCatalog, errorCode, keyPrefix) ??
    formatCatalogCode(
      atomErrorCatalog,
      normalized.type,
      keyPrefix,
      errorCode ?? normalized.type,
    );
  if (cataloguedError) {
    return cataloguedError;
  }

  return {
    level: getLevelForUncataloguedCode(errorCode),
    message: operationMessage ?? defaultMessage,
    description: errorCode ? formatReferenceCode(errorCode) : undefined,
    key,
  };
}

export function resolveUserFacingSuccess(
  options?: UserFacingSuccessOptions,
): FormattedError {
  const keyPrefix = options?.keyPrefix ?? "success";
  const message =
    options?.successMessage ??
    (options?.operationContext
      ? getOperationContextSuccessMessage(options.operationContext)
      : undefined) ??
    translate(
      "framework.errors.actionSucceeded",
      "Aktion erfolgreich abgeschlossen.",
    );

  return {
    level: "success",
    message,
    key: options?.operationContext
      ? `${keyPrefix}:operation-success`
      : `${keyPrefix}:success`,
  };
}
